import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Moon, Sun, Menu, X } from 'lucide-react';
import TekMark from './TekMark';
import { useTheme } from '../context/ThemeContext';
import useReveal from '../hooks/useReveal';

/**
 * The full landing-page top navigation, extracted so every marketing
 * page can use the same chrome. Previously the landing page had this
 * rich nav while pricing / product / blog / glossary / trust pages
 * shipped with stripped-down inline nav blocks — users could feel the
 * discontinuity.
 *
 * The four section links (Features / How it works / Principles +
 * Pricing) behave differently depending on the current route:
 *
 *  - On `/`: prevent default, smooth-scroll to the anchor in-page.
 *  - On any other route: navigate to `/#section` and let
 *    `useScrollToLandingHash` (mounted on LandingPage) handle the
 *    scroll after the route renders.
 *
 * This means the nav looks identical everywhere, and the section
 * links always work — they just take a route hop when you're not on
 * the homepage already.
 */
export default function LandingNav() {
  // Reveal-on-scroll runs once per route change. LandingNav is mounted
  // on every marketing page, so this single call gives every page the
  // observer it needs — fixes the "all .tk-reveal content invisible
  // forever on non-landing pages" bug that previously only LandingPage
  // worked around with a locally-scoped useReveal.
  useReveal();

  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === '/';

  const [menuOpen, setMenuOpen] = React.useState(false);
  const close = React.useCallback(() => setMenuOpen(false), []);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen, close]);

  // Anchor click handler. If we're already on `/`, intercept and
  // smooth-scroll in-page (no route change). If we're elsewhere,
  // intercept and navigate to /#section — LandingPage's hash hook
  // takes over from there.
  const goToSection = React.useCallback((id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (onLanding) {
      requestAnimationFrame(() => {
        const target = document.getElementById(id);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else {
      navigate(`/#${id}`);
    }
  }, [onLanding, navigate]);

  return (
    <header className="tk-nav-wrap relative z-30">
      <div className="tk-nav-pill">
        <Link to="/" className="tk-nav-brand flex items-center group">
          <TekMark size={26} />
        </Link>

        <span className="tk-nav-divider" aria-hidden />

        <nav className="tk-nav-links" aria-label="Primary">
          <a href="/#features"   onClick={goToSection('features')}>Features</a>
          <a href="/#process"    onClick={goToSection('process')}>How it works</a>
          <a href="/#principles" onClick={goToSection('principles')}>Principles</a>
          <Link to="/pricing">Pricing</Link>
        </nav>

        <span className="tk-nav-divider" aria-hidden />

        <button
          type="button"
          className="tk-nav-theme"
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-pressed={theme === 'dark'}
          onClick={toggleTheme}
        >
          {theme === 'dark'
            ? <Sun size={16} strokeWidth={1.8} />
            : <Moon size={16} strokeWidth={1.8} />}
        </button>
        <Link to="/signup" className="tk-nav-cta">
          Get in Touch
        </Link>

        <button
          type="button"
          className="tk-nav-burger"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="tk-mobile-menu"
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {menuOpen && (
        <>
          <div className="tk-nav-mobile-backdrop" onClick={close} aria-hidden />
          <div id="tk-mobile-menu" className="tk-nav-mobile-sheet" role="dialog" aria-modal="true" aria-label="Site navigation">
            <a href="/#features"   onClick={goToSection('features')}>Features</a>
            <a href="/#process"    onClick={goToSection('process')}>How it works</a>
            <a href="/#principles" onClick={goToSection('principles')}>Principles</a>
            <Link to="/pricing"  onClick={close}>Pricing</Link>
            <Link to="/blog"     onClick={close}>Blog</Link>
            <Link to="/glossary" onClick={close}>Glossary</Link>
            <Link to="/signin"   onClick={close} className="tk-nav-mobile-secondary">Sign in</Link>
            <Link to="/signup"   onClick={close} className="tk-nav-mobile-cta">Get in Touch</Link>
          </div>
        </>
      )}
    </header>
  );
}

/**
 * Hook for LandingPage: when the route mounts with a `#section` hash
 * (e.g. after a /#features link from another page), scroll the named
 * section into view. Runs once on mount because the ScrollToHash
 * behaviour for in-page anchors is handled by LandingNav itself.
 */
export function useScrollToLandingHash() {
  const location = useLocation();
  React.useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Defer to next frame so the section actually exists in the DOM
    // by the time we look for it.
    requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash]);
}
